import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useSearchParams } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from '@/contexts/AuthContext';
import { GlobalWebSocketProvider } from '@/contexts/GlobalWebSocketContext';
import { WalletProvider } from '@/contexts/WalletContext';
import { NotificationProvider } from '@/contexts/NotificationContext';
import { GameProvider } from '@/contexts/GameContext';
import { DesignProvider } from '@/contexts/DesignContext';
import { saveAffiliateRef } from '@/lib/affiliate';
import { OnboardingProvider } from '@/contexts/OnboardingContext';
import { DOIProvider } from '@/contexts/DOIContext';
import { DOIVerificationModal } from '@/components/verification/DOIVerificationModal';
import { ServiceWorkerProvider } from '@/components/ServiceWorkerProvider';
import { Navigation } from '@/components/layout/Navigation';
import { MobileHeader } from '@/components/layout/MobileHeader';
import { AdminSidebarProvider } from '@/contexts/AdminSidebarContext';
import { AdminFloatingButton } from '@/components/layout/AdminFloatingButton';
import { EditModeProvider } from '@/contexts/EditModeContext';
const Index = React.lazy(() => import('@/pages/Index'));
const Characters = React.lazy(() => import('@/pages/Characters'));
const CharacterProfile = React.lazy(() => import('@/pages/CharacterProfile'));
const CharacterLandingPage = React.lazy(() => import('@/pages/CharacterLandingPage'));
const MagicLinkLandingPage = React.lazy(() => import('@/pages/MagicLinkLandingPage'));
const Chat = React.lazy(() => import('@/pages/Chat'));
const ChatOverview = React.lazy(() => import('@/pages/ChatOverview'));
const Auth = React.lazy(() => import('@/pages/Auth'));
const Dashboard = React.lazy(() => import('@/pages/Dashboard'));
const DailyChallenges = React.lazy(() => import('@/pages/DailyChallenges'));
const Profile = React.lazy(() => import('@/pages/Profile'));
const PartnerBillingDashboard = React.lazy(() => import('@/pages/PartnerBillingDashboard'));
import { ProtectedRoute } from '@/components/ProtectedRoute';
// removed unused AdminLayout and Toaster imports
import { ToastProvider } from '@/components/notifications/ToastProvider';
import { StickyHeaderProvider, useStickyHeaderContent } from '@/contexts/StickyHeaderContext';
import { cn } from '@/lib/utils';
import { ScrollToTop } from '@/components/layout/ScrollToTop';
const LocationSettings = React.lazy(() => import('@/pages/LocationSettings'));
const Wallet = React.lazy(() => import('@/pages/Wallet'));
const Shop = React.lazy(() => import('@/pages/Shop'));
const Swipe = React.lazy(() => import('@/pages/Swipe'));
const Matches = React.lazy(() => import('@/pages/Matches'));
const Favorites = React.lazy(() => import('@/pages/Favorites'));
const Support = React.lazy(() => import('@/pages/Support'));
// removed unused Games import
const NotFound = React.lazy(() => import('@/pages/NotFound'));
import { LoadingBar } from '@/components/ui/loading-bar';
import { PageViewTracker } from '@/components/analytics/PageViewTracker';
import { SessionLifecycleProvider } from '@/components/analytics/SessionLifecycleProvider';
import { AnonymousTrackingProvider } from '@/components/analytics/AnonymousTrackingProvider';
import EmailVerification from '@/pages/EmailVerification';
import ResetPasswordRequest from '@/pages/ResetPasswordRequest';
import ResetPassword from '@/pages/ResetPassword';
import NotificationCenter from '@/pages/NotificationCenter';
import NotificationSettings from '@/pages/NotificationSettings';
import AchievementDashboard from '@/pages/AchievementDashboard';
import CharacterRelationship from '@/pages/CharacterRelationship';
import ProfileVisits from '@/pages/ProfileVisits';
import FollowUpSettingsPage from '@/pages/FollowUpSettingsPage';
import CreatorSpace from '@/pages/CreatorSpace';
import CreatorProfile from '@/pages/CreatorProfile';
import BillingResultSuccess from '@/pages/BillingResultSuccess';
import BillingResultCancel from '@/pages/BillingResultCancel';
import BillingPending from '@/pages/BillingPending';
import PaymentResultHandler from '@/components/billing/PaymentResultHandler';
import { SupportBubble } from '@/components/support/SupportBubble';
import { Footer } from '@/components/layout/Footer';
import { InlineEditHintBar } from '@/components/i18n/InlineEditHintBar';
import { GlobalTextEditor } from '@/components/i18n/GlobalTextEditor';
import { ClickToEditListener } from '@/components/i18n/ClickToEditListener';
// import { InlineEditOverlayScanner } from '@/components/i18n/InlineEditOverlayScanner';
const LegalImpressum = React.lazy(() => import('@/pages/LegalImpressum'));
const LegalDatenschutz = React.lazy(() => import('@/pages/LegalDatenschutz'));
const LegalAGB = React.lazy(() => import('@/pages/LegalAGB'));
const MySubscription = React.lazy(() => import('@/pages/MySubscription'));

// Admin Layout und Routes
import { AdminLayout } from '@/components/admin/layout';
import { adminRoutes } from '@/routes/AdminRoutes';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      staleTime: 60_000, // 1 minute default
      gcTime: 5 * 60 * 1000, // 5 minutes garbage collection
    },
  },
});

/**
 * Inner component that renders the sticky header from context.
 * Must be inside StickyHeaderProvider to access the context.
 *
 * Uses position: fixed (like StickyMiniGallery on CharacterProfile) because
 * position: sticky doesn't work reliably in nested flex containers.
 * Fixed positioning is viewport-relative and works independently of scroll hierarchy.
 */
function StickyHeaderRenderer() {
  const stickyHeader = useStickyHeaderContent();

  if (!stickyHeader) return null;

  return (
    <>
      {/* Fixed header bar */}
      <div
        className={cn(
          'app-sticky-header',
          // Fixed position - directly under the fixed Navigation (same approach as StickyMiniGallery)
          'fixed left-0 right-0 z-40',
          // Mobile: top-0, Desktop: below nav (top-16 = 4rem = nav height)
          'top-0 md:top-16',
          // Glaseffekt (consistent with design system)
          'bg-white/5 backdrop-blur-xl border-b border-white/10',
          // Padding
          'py-token-sm sm:py-token-md px-token-md sm:px-token-lg'
        )}
      >
        <div className="w-full max-w-screen-xl mx-auto">
          {stickyHeader}
        </div>
      </div>
      {/* Spacer to push content below the fixed header */}
      <div className="sticky-header-spacer h-[120px] sm:h-[140px]" />
    </>
  );
}

function AffiliateRefCapture() {
  const [searchParams] = useSearchParams();
  useEffect(() => {
    const ref = searchParams.get('ref');
    if (ref) {
      saveAffiliateRef(ref);
    }
  }, [searchParams]);
  return null;
}

function App() {
  return (
    <BrowserRouter
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true
      }}
    >
      <QueryClientProvider client={queryClient}>
        <ServiceWorkerProvider>
          <DesignProvider>
            <AuthProvider>
              <EditModeProvider>
                <GlobalWebSocketProvider>
                  <NotificationProvider>
                    <ToastProvider>
                      <WalletProvider>
                        <GameProvider>
                          <DOIProvider>
                          <OnboardingProvider>
                        <DOIVerificationModal />
                        <div className="app-wrapper design-page-gradient">
                          <ScrollToTop />
                          <LoadingBar />
                          <AffiliateRefCapture />
                          <PageViewTracker />
                          <SessionLifecycleProvider />
                          <AnonymousTrackingProvider />
                          <AdminSidebarProvider>
                            <div className="sticky top-0 z-50 w-full">
                              <Navigation />
                            </div>
                            <AdminFloatingButton />
                            <InlineEditHintBar />
                            <GlobalTextEditor />
                            <ClickToEditListener />
                          
                          {/* Chatbubble sichtbar für User; bei Admins ausgeblendet via Prop */}
                          <SupportBubble hideForAdmins />
                          {/* Entfernt: doppelter Toaster. ToastProvider rendert den Toaster global. */}
                          <StickyHeaderProvider>
                          <main className="app-main">
                            {/* Sticky Header - rendered from context, direct child of scroll container */}
                            <StickyHeaderRenderer />
                <React.Suspense fallback={<div className="p-token-md text-sm text-muted-foreground">Laden…</div>}>
                <Routes>
                  {/* Billing redirect endpoints */}
                  <Route path="/billing/success" element={<BillingResultSuccess />} />
                  <Route path="/billing/cancel" element={<BillingResultCancel />} />
                  <Route path="/billing/pending" element={<BillingPending />} />
                  {/* Public Legal Pages */}
                  <Route path="/impressum" element={<LegalImpressum />} />
                  <Route path="/datenschutz" element={<LegalDatenschutz />} />
                  <Route path="/agb" element={<LegalAGB />} />
                  <Route path="/" element={<Index />} />
                  <Route path="/characters" element={<Characters />} />
                  <Route path="/c/:slug" element={<CharacterLandingPage />} />
                  <Route path="/msg/:slug" element={<MagicLinkLandingPage />} />
                  <Route path="/characters/:id" element={<CharacterProfile />} />
                  <Route path="/character/:id/relationship" element={<ProtectedRoute><CharacterRelationship /></ProtectedRoute>} />
                  {/* Chat Routes - optionaler characterId Parameter für persistentes Layout */}
                  <Route path="/chats/:characterId?" element={<ProtectedRoute><ChatOverview /></ProtectedRoute>} />
                  <Route path="/chat/:characterId" element={<ProtectedRoute><Chat /></ProtectedRoute>} />
                  <Route path="/auth" element={<Auth />} />
                  <Route path="/verify-email" element={<EmailVerification />} />
                  <Route path="/forgot-password" element={<ResetPasswordRequest />} />
                  <Route path="/reset-password" element={<ResetPassword />} />
                  <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
                  <Route path="/daily-challenges" element={<ProtectedRoute><DailyChallenges /></ProtectedRoute>} />
                  <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
                  <Route path="/profile/visits" element={<ProtectedRoute><ProfileVisits /></ProtectedRoute>} />
                  <Route path="/profile/follow-up" element={<ProtectedRoute><FollowUpSettingsPage /></ProtectedRoute>} />
                  <Route path="/settings/location" element={<ProtectedRoute><LocationSettings /></ProtectedRoute>} />
                  <Route path="/wallet" element={<ProtectedRoute><Wallet /></ProtectedRoute>} />
                  <Route path="/shop" element={<ProtectedRoute><Shop /></ProtectedRoute>} />
                  <Route path="/notifications" element={<ProtectedRoute><NotificationCenter /></ProtectedRoute>} />
                  <Route path="/notifications/settings" element={<ProtectedRoute><NotificationSettings /></ProtectedRoute>} />
                  <Route path="/achievements" element={<ProtectedRoute><AchievementDashboard /></ProtectedRoute>} />
                  
                  {/* Creator Space Routes */}
                  <Route path="/creator-space" element={<ProtectedRoute><CreatorSpace /></ProtectedRoute>} />
                  {/* Redirect old /my-subscriptions to new unified page with creators tab */}
                  <Route path="/my-subscriptions" element={<Navigate to="/subscription?tab=creators" replace />} />
                  <Route path="/subscription" element={<ProtectedRoute><React.Suspense fallback={<div className="p-token-md text-sm text-muted-foreground">Laden…</div>}><MySubscription /></React.Suspense></ProtectedRoute>} />
                  <Route path="/creator/:creatorId" element={<ProtectedRoute><CreatorProfile /></ProtectedRoute>} />
                  
                  {/* Swipe Routes */}
                  <Route path="/swipe" element={<ProtectedRoute><Swipe /></ProtectedRoute>} />
                  <Route path="/matches" element={<ProtectedRoute><Matches /></ProtectedRoute>} />
                  <Route path="/favorites" element={<ProtectedRoute><Favorites /></ProtectedRoute>} />
                  <Route path="/support" element={<Support />} />

                  {/* Game Routes - temporär deaktiviert für späteren Einsatz */}
                  {/* <Route path="/games" element={<ProtectedRoute><Games /></ProtectedRoute>} /> */}
                  {/* <Route path="/games/:gameId" element={<ProtectedRoute><Games /></ProtectedRoute>} /> */}
                  {/* Redirect Games to Dashboard until feature is ready */}
                  <Route path="/games" element={<Navigate to="/dashboard" replace />} />
                  <Route path="/games/*" element={<Navigate to="/dashboard" replace />} />

                  {/* Partner Billing (separate, nicht Admin) */}
                  <Route path="/partner/billing" element={
                    <ProtectedRoute requiredRole="partner_admin">
                      <PartnerBillingDashboard />
                    </ProtectedRoute>
                  } />

                  {/* Admin Routes - Nested mit persistentem Layout */}
                  <Route path="/admin/*" element={<AdminLayout />}>
                    {adminRoutes}
                  </Route>
                  
                  {/* 404 Catch-all route */}
                  <Route path="*" element={<NotFound />} />
                </Routes>
                </React.Suspense>
                          </main>
                          </StickyHeaderProvider>
                          {/* Global Footer */}
                          <Footer />
                          {/* Global modal handler for payment results */}
                          <PaymentResultHandler />
                          <MobileHeader />
                          </AdminSidebarProvider>
                        </div>
                          </OnboardingProvider>
                          </DOIProvider>
                        </GameProvider>
                      </WalletProvider>
                    </ToastProvider>
                  </NotificationProvider>
                </GlobalWebSocketProvider>
              </EditModeProvider>
            </AuthProvider>
          </DesignProvider>
        </ServiceWorkerProvider>
      </QueryClientProvider>
    </BrowserRouter>
  );
}

export default App;
